// src/services/tokenPriceService.js

class TokenPriceService {
  constructor() {
    this.cache = {
      prices: null,
      lastUpdate: null,
      CACHE_DURATION: 30000 // 30 segundos
    };
  }

  // Obtener todos los precios con fallbacks
  async getAllPrices() {
    // Verificar cache
    if (this.cache.prices && this.cache.lastUpdate &&
        Date.now() - this.cache.lastUpdate < this.cache.CACHE_DURATION) {
      console.log('📦 Usando precios en cache');
      return this.cache.prices;
    }

    try {
      console.log('🔄 Obteniendo precios desde múltiples fuentes...');

      // Obtener precios en paralelo
      const [solPrice, csbtcData] = await Promise.allSettled([
        this._getSolanaPrice(),
        this._getCSBTCPriceData()
      ]);

      const prices = {
        solPrice: solPrice.status === 'fulfilled' ? solPrice.value : this._getFallbackSolPrice(),
        csbtcPrice: csbtcData.status === 'fulfilled' ? csbtcData.value.price : this._getFallbackCSBTCPrice(),
        csbtcPriceChange24h: csbtcData.status === 'fulfilled' ? csbtcData.value.priceChange24h : 0,
        csbtcVolume24h: csbtcData.status === 'fulfilled' ? csbtcData.value.volume24h : 0
      };

      // Actualizar cache
      this.cache.prices = prices;
      this.cache.lastUpdate = Date.now();

      console.log('✅ Precios obtenidos:', prices);
      return prices;

    } catch (error) {
      console.error('❌ Error crítico obteniendo precios:', error);
      return this._getFallbackPrices();
    }
  }

  // Obtener precio de Solana desde múltiples fuentes
  async _getSolanaPrice() {
    const sources = [
      this._getSolanaFromJupiter.bind(this),
      this._getSolanaFromRaydium.bind(this),
      this._getSolanaFromCoinGecko.bind(this)
    ];

    for (const source of sources) {
      try {
        const price = await source();
        if (price && price > 0) {
          console.log(`✅ SOL price from ${source.name}: $${price}`);
          return price;
        }
      } catch (error) {
        console.warn(`⚠️ ${source.name} failed:`, error.message);
        continue;
      }
    }

    throw new Error('Todas las fuentes de SOL fallaron');
  }

  // Obtener datos de CSBTC desde múltiples fuentes
  async _getCSBTCPriceData() {
    const sources = [
      this._getCSBTCFromRaydium.bind(this),
      this._getCSBTCFromJupiter.bind(this),
      this._getCSBTCFromDexScreener.bind(this)
    ];

    for (const source of sources) {
      try {
        const data = await source();
        if (data && data.price > 0) {
          console.log(`✅ CSBTC data from ${source.name}:`, data);
          return data;
        }
      } catch (error) {
        console.warn(`⚠️ ${source.name} failed:`, error.message);
        continue;
      }
    }

    throw new Error('Todas las fuentes de CSBTC fallaron');
  }

  // ========== IMPLEMENTACIONES DE APIS ==========

  // Jupiter API para SOL
  async _getSolanaFromJupiter() {
    try {
      console.log('🔍 Obteniendo SOL desde Jupiter...');
      const response = await fetch('https://quote-api.jup.ag/v6/quote?inputMint=So11111111111111111111111111111111111111112&outputMint=EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v&amount=1000000000', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`Jupiter SOL HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.data || !data.data[0]) {
        throw new Error('Respuesta inválida de Jupiter');
      }

      const price = parseFloat(data.data[0].outAmount) / 1000000; // USDC tiene 6 decimales
      return price / 1000; // Ajustar por el amount solicitado

    } catch (error) {
      console.error('❌ Jupiter SOL failed:', error.message);
      throw new Error(`Jupiter failed: ${error.message}`);
    }
  }

  // Raydium API para SOL
  async _getSolanaFromRaydium() {
    try {
      console.log('🔍 Obteniendo SOL desde Raydium...');
      const response = await fetch('https://api.raydium.io/v2/main/price', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`Raydium SOL HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data['So11111111111111111111111111111111111111112']) {
        throw new Error('SOL no encontrado en respuesta de Raydium');
      }

      const price = parseFloat(data['So11111111111111111111111111111111111111112'].price);

      if (!price || price <= 0) {
        throw new Error('Precio de SOL inválido de Raydium');
      }

      return price;

    } catch (error) {
      console.error('❌ Raydium SOL failed:', error.message);
      throw new Error(`Raydium failed: ${error.message}`);
    }
  }

  // CoinGecko como fallback para SOL
  async _getSolanaFromCoinGecko() {
    try {
      console.log('🔍 Obteniendo SOL desde CoinGecko...');
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd', {
        method: 'GET',
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`CoinGecko HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.solana || !data.solana.usd) {
        throw new Error('Respuesta inválida de CoinGecko');
      }

      return parseFloat(data.solana.usd);

    } catch (error) {
      console.error('❌ CoinGecko failed:', error.message);
      throw new Error(`CoinGecko failed: ${error.message}`);
    }
  }

  // Raydium API para CSBTC
  async _getCSBTCFromRaydium() {
    try {
      console.log('🔍 Obteniendo CSBTC desde Raydium...');
      const response = await fetch('https://api.raydium.io/v2/main/price', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`Raydium CSBTC HTTP error: ${response.status}`);
      }

      const data = await response.json();
      const csbtcData = data['9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR'];

      if (!csbtcData) {
        throw new Error('CSBTC no encontrado en Raydium');
      }

      return {
        price: parseFloat(csbtcData.price),
        priceChange24h: parseFloat(csbtcData.price24hChange || 0),
        volume24h: parseFloat(csbtcData.volume24h || 0)
      };

    } catch (error) {
      console.error('❌ Raydium CSBTC failed:', error.message);
      throw new Error(`Raydium failed: ${error.message}`);
    }
  }

  // Jupiter API para CSBTC
  async _getCSBTCFromJupiter() {
    try {
      console.log('🔍 Obteniendo CSBTC desde Jupiter...');
      const response = await fetch('https://quote-api.jup.ag/v6/quote?inputMint=9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR&outputMint=EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v&amount=1000000', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`Jupiter CSBTC HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.data || !data.data[0]) {
        throw new Error('Respuesta inválida de Jupiter para CSBTC');
      }

      const price = parseFloat(data.data[0].outAmount) / 1000000; // USDC tiene 6 decimales

      return {
        price: price,
        priceChange24h: 0, // Jupiter no proporciona este dato
        volume24h: 0
      };

    } catch (error) {
      console.error('❌ Jupiter CSBTC failed:', error.message);
      throw new Error(`Jupiter CSBTC failed: ${error.message}`);
    }
  }

  // DexScreener como fallback para CSBTC
  async _getCSBTCFromDexScreener() {
    try {
      console.log('🔍 Obteniendo CSBTC desde DexScreener...');
      const response = await fetch('https://api.dexscreener.com/latest/dex/pairs/solana/9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR', {
        method: 'GET',
        signal: AbortSignal.timeout(8000)
      });

      if (!response.ok) {
        throw new Error(`DexScreener HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.pairs || !data.pairs[0]) {
        throw new Error('No se encontraron pairs en DexScreener');
      }

      const pair = data.pairs[0];

      return {
        price: parseFloat(pair.priceUsd),
        priceChange24h: parseFloat(pair.priceChange.h24 || 0),
        volume24h: parseFloat(pair.volume.h24 || 0)
      };

    } catch (error) {
      console.error('❌ DexScreener failed:', error.message);
      throw new Error(`DexScreener failed: ${error.message}`);
    }
  }

  // ========== FALLBACKS ==========

  _getFallbackPrices() {
    console.log('🔄 Usando precios de fallback');
    return {
      solPrice: this._getFallbackSolPrice(),
      csbtcPrice: this._getFallbackCSBTCPrice(),
      csbtcPriceChange24h: 0,
      csbtcVolume24h: 0
    };
  }

  _getFallbackSolPrice() {
    // Precio aproximado de SOL como fallback
    return 127.52; // $100 USD como fallback
  }

  _getFallbackCSBTCPrice() {
    // Precio aproximado de CSBTC como fallback
    return 0.001261; // ✅ PRECIO REAL DE CSBTC (~$0.001261)
  }

  // Limpiar cache
  clearCache() {
    this.cache.prices = null;
    this.cache.lastUpdate = null;
  }
}

// Exportar instancia singleton
export const tokenPriceService = new TokenPriceService();

// Exportar clase para testing
export { TokenPriceService };
